import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useShop } from '@/hooks/useShop';

export interface CapitalLog {
  id: string;
  type: 'deposit' | 'withdraw';
  amount: number;
  note: string;
  createdAt: string;
}

// Quỹ vốn theo tiệm — lưu trong DB (shops.total_capital + capital_logs), đồng bộ mọi thiết bị
export function useCapital() {
  const { user } = useAuth();
  const { shop } = useShop();
  const [totalCapital, setTotalCapital] = useState(0);
  const [capitalLogs, setCapitalLogs] = useState<CapitalLog[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user || !shop) {
      setTotalCapital(0);
      setCapitalLogs([]);
      setLoading(false);
      return;
    }
    setTotalCapital(shop.totalCapital);
    const { data } = await supabase
      .from('capital_logs')
      .select('id, type, amount, note, created_at')
      .eq('shop_id', shop.id)
      .order('created_at', { ascending: false })
      .limit(200);
    setCapitalLogs(
      (data || []).map(r => ({
        id: r.id as string,
        type: r.type as 'deposit' | 'withdraw',
        amount: Number(r.amount),
        note: (r.note as string) || '',
        createdAt: r.created_at as string,
      }))
    );
    setLoading(false);
  }, [user, shop]);

  useEffect(() => {
    load();
  }, [load]);

  const addTransaction = useCallback(async (type: 'deposit' | 'withdraw', amount: number, note: string) => {
    if (!user || !shop || amount <= 0) return { error: new Error('Dữ liệu không hợp lệ') };
    if (type === 'withdraw' && amount > totalCapital) {
      return { error: new Error('Số tiền rút vượt quá tổng vốn hiện có') };
    }

    const { error: logErr } = await supabase.from('capital_logs').insert({
      shop_id: shop.id,
      type,
      amount,
      note,
      created_by: user.id,
    });
    if (logErr) return { error: logErr };

    const newTotal = type === 'deposit' ? totalCapital + amount : totalCapital - amount;
    const { error: updErr } = await supabase
      .from('shops')
      .update({ total_capital: newTotal })
      .eq('id', shop.id);
    if (updErr) return { error: updErr };

    await load();
    window.dispatchEvent(new Event('shop-updated'));
    return { error: null };
  }, [user, shop, totalCapital, load]);

  // Đặt trực tiếp tổng vốn (ô nhập "Tổng vốn kinh doanh" trong Cài đặt)
  const setCapital = useCallback(async (amount: number) => {
    if (!user || !shop || amount < 0) return { error: new Error('Dữ liệu không hợp lệ') };
    const { error } = await supabase
      .from('shops')
      .update({ total_capital: amount, updated_at: new Date().toISOString() })
      .eq('id', shop.id);
    if (error) return { error };
    setTotalCapital(amount);
    window.dispatchEvent(new Event('shop-updated'));
    return { error: null };
  }, [user, shop]);

  return { totalCapital, capitalLogs, loading, addTransaction, setCapital, reload: load };
}
